/**
 * Compose a branded install-poster PNG and return it as a Blob.
 *
 * Why a canvas (not a JSX-to-image lib like html2canvas):
 *   * Zero new deps. We already have qrcode.react's QRCodeCanvas.
 *   * Deterministic pixel output — the poster looks identical on
 *     every device, no font-fallback surprises, no missing CSS.
 *   * High DPI is trivial: just bump the canvas dimensions.
 *
 * Poster is a 1200×1600 (3:4) portrait flyer:
 *   • Cream background with subtle border
 *   • Logo (~256px) centered near the top
 *   • "Armstrong K9 Registry" wordmark
 *   • "Scan to install the app" subtitle
 *   • QR code (~720px) centered
 *   • Install URL beneath
 *   • Footer tagline + small print
 *
 * Returns a PNG Blob suitable for:
 *   • <a download> attribute (save to disk)
 *   • new File([blob], 'name.png', {type:'image/png'}) for the
 *     Web Share API
 *   • email attachment via mailto: (no — mailto can't attach;
 *     we use a separate flow for that)
 */

const POSTER_W = 1200;
const POSTER_H = 1600;
const QR_PX = 760;
const LOGO_PX = 220;
const CREAM = "#FBF7EC";
const GOLD = "#C9A227";
const INK = "#0E0E0E";
const SUB = "#6B6B6B";
const BORDER = "#E2DAC2";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth) {
      cur = test;
    } else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

/**
 * @param {HTMLCanvasElement} qrCanvas — QRCodeCanvas's mounted canvas
 * @param {string} url — install URL printed under the QR
 * @param {string} logoSrc — path to logo image (defaults to /logo-256.png)
 */
export async function composeInstallPoster(qrCanvas, url, logoSrc = "/logo-256.png") {
  if (!qrCanvas) throw new Error("composeInstallPoster: missing QR canvas");
  const canvas = document.createElement("canvas");
  canvas.width = POSTER_W;
  canvas.height = POSTER_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("composeInstallPoster: canvas 2d unavailable");

  // Background
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, POSTER_W, POSTER_H);

  // Outer hairline border
  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 4;
  ctx.strokeRect(40, 40, POSTER_W - 80, POSTER_H - 80);

  // Gold top accent band
  ctx.fillStyle = GOLD;
  ctx.fillRect(40, 40, POSTER_W - 80, 8);

  // ---- Header: logo + wordmark ------------------------------------
  let logoLoaded = false;
  try {
    const logo = await loadImage(logoSrc);
    const lx = (POSTER_W - LOGO_PX) / 2;
    const ly = 110;
    ctx.drawImage(logo, lx, ly, LOGO_PX, LOGO_PX);
    logoLoaded = true;
  } catch {
    // Logo failed to load (CORS, missing file). Fall back to a
    // textual wordmark only — better than a broken-image hole.
  }

  // Wordmark
  const wordmarkY = logoLoaded ? 110 + LOGO_PX + 60 : 200;
  ctx.fillStyle = INK;
  ctx.textAlign = "center";
  ctx.font = '500 56px "Spectral", "Times New Roman", serif';
  ctx.fillText("Armstrong K9 Registry", POSTER_W / 2, wordmarkY);

  // Eyebrow
  ctx.fillStyle = GOLD;
  ctx.font = 'bold 22px "Inter", system-ui, sans-serif';
  ctx.letterSpacing = "0.3em";
  ctx.fillText("SCAN TO INSTALL THE APP", POSTER_W / 2, wordmarkY + 56);

  // ---- QR code ----------------------------------------------------
  const qrX = (POSTER_W - QR_PX) / 2;
  const qrY = wordmarkY + 100;
  // White background tile under the QR with a hairline frame so the
  // black modules contrast cleanly even if it&apos;s printed on
  // cream paper.
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(qrX - 24, qrY - 24, QR_PX + 48, QR_PX + 48);
  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 2;
  ctx.strokeRect(qrX - 24, qrY - 24, QR_PX + 48, QR_PX + 48);
  // Draw the live QRCodeCanvas onto the poster
  ctx.drawImage(qrCanvas, qrX, qrY, QR_PX, QR_PX);

  // ---- URL caption ------------------------------------------------
  const urlY = qrY + QR_PX + 80;
  ctx.fillStyle = SUB;
  ctx.font = '400 24px "Inter", system-ui, sans-serif';
  ctx.fillText("Or visit", POSTER_W / 2, urlY);
  ctx.fillStyle = INK;
  ctx.font = '600 32px "JetBrains Mono", "Courier New", monospace';
  // URL might be long on preview/staging. Wrap if it exceeds 90% of width.
  const urlLines = wrapText(ctx, url, POSTER_W * 0.88);
  urlLines.forEach((line, i) => {
    ctx.fillText(line, POSTER_W / 2, urlY + 40 + i * 38);
  });

  // ---- Footer -----------------------------------------------------
  const footerY = POSTER_H - 160;
  ctx.fillStyle = INK;
  ctx.font = 'italic 28px "Spectral", "Times New Roman", serif';
  const tagline = "The uncompromising standard for purpose-bred dogs.";
  ctx.fillText(tagline, POSTER_W / 2, footerY);

  // Small print
  ctx.fillStyle = SUB;
  ctx.font = '400 18px "Inter", system-ui, sans-serif';
  ctx.fillText(
    "Free · No App Store needed · Works on iPhone & Android",
    POSTER_W / 2,
    footerY + 44,
  );

  // Bottom gold band (mirror of top)
  ctx.fillStyle = GOLD;
  ctx.fillRect(40, POSTER_H - 48, POSTER_W - 80, 8);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("canvas.toBlob returned null"));
      },
      "image/png",
      0.95,
    );
  });
}

/**
 * Trigger a browser download for a Blob with a given filename.
 * Cleans up the object URL on the next tick.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Returns true if the browser supports `navigator.share()` with files.
 * Some browsers expose `share()` only for URLs / text — not files.
 */
export function canShareFiles(file) {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  );
}
